import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuTrigger, MatMenu } from "@angular/material/menu";
import {
  TicketRecallService,
  RecallResponse
} from "@app/services/http/ticket-recall.service";
import { TicketRecallStatus } from "@app/models/enums/ticket-recall-status.enum";
import { TicketActionDialogComponent } from "@app/view/planning/ticket-action-dialog/ticket-action-dialog.component";
import { TicketRecallCancelDialogComponent } from "@app/view/planning/ticket-recall-cancel-dialog/ticket-recall-cancel-dialog.component";
import { StompRouterService } from "@app/services/webstomp/stomp-router.service";
import { Subscription } from "rxjs";
import { MatIconButton } from "@angular/material/button";
import { NgClass } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MomentModule } from "ngx-moment";

@Component({
  selector: "app-recall-tool",
  templateUrl: "./recall-tool.component.html",
  styleUrls: ["./recall-tool.component.scss"],
  imports: [
    MatIconButton,
    MatMenuTrigger,
    NgClass,
    MatIcon,
    MatMenu,
    MomentModule
  ]
})
export class RecallToolComponent implements OnInit, OnDestroy {
  private recallService = inject(TicketRecallService);
  private stompRouterService = inject(StompRouterService);
  dialog = inject(MatDialog);

  @Input()
  public fullPageMode: boolean = false;

  @ViewChild("recallTrigger", { read: MatMenuTrigger, static: false })
  public recallTriggerBtn: MatMenuTrigger;

  @Output()
  emitRecallCount = new EventEmitter<number>();

  hasRecalls = false;
  recallCount: number = 0;
  activeRecalls: RecallResponse[] = [];
  private stompSubscription: Subscription;

  ngOnInit() {
    // Subscribe to STOMP recall updates
    this.stompRouterService.addSubscription("/topic/ticketRecall/");
    this.stompSubscription = this.stompRouterService
      .getEntityStream("TicketRecall")
      .subscribe((event) => {
        if (event == null) return; // skip BehaviorSubject initial null
        // Delay reload slightly — JPA @PostPersist fires before the transaction
        // commits, so the STOMP message can arrive before data is queryable.
        setTimeout(() => this.loadActiveRecalls(), 600);
      });

    // Initial load
    this.loadActiveRecalls();
  }

  ngOnDestroy() {
    if (this.stompSubscription) {
      this.stompSubscription.unsubscribe();
    }
  }

  loadActiveRecalls() {
    this.recallService.getActiveRecalls().then((recalls) => {
      this.activeRecalls = recalls;
      this.recallCount = recalls.length;
      this.hasRecalls = this.recallCount > 0;
      this.emitRecallCount.emit(this.recallCount);
    });
  }

  cancelRecall(recall: RecallResponse, event: Event) {
    event.stopPropagation();
    const cancelRef = this.dialog.open(TicketRecallCancelDialogComponent);
    cancelRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.recallService.cancelRecall(recall.id).then(() => {
          this.loadActiveRecalls();
        });
      }
    });
  }

  openTicketDialog(recall: RecallResponse) {
    if (recall.ticket?.id) {
      this.dialog.open(TicketActionDialogComponent, {
        hasBackdrop: true,
        width: "500px",
        panelClass: "toolbar-popup-dialog",
        data: {
          ticketId: recall.ticket.id
        }
      });
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case TicketRecallStatus.CONTACTING:
        return "Contacting operative...";
      case TicketRecallStatus.PROCESSING:
        return "Processing recall";
      case TicketRecallStatus.TIMED_OUT:
        return "Timed out - no response";
      default:
        return status;
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case TicketRecallStatus.CONTACTING:
        return "fa-spinner fa-spin";
      case TicketRecallStatus.PROCESSING:
        return "fa-gear fa-spin";
      case TicketRecallStatus.TIMED_OUT:
        return "fa-clock";
      default:
        return "fa-rotate-left";
    }
  }

  onMenuOpen() {
    this.loadActiveRecalls();
  }
}
